import React, { useState } from 'react';
import { Box, Typography, Button, Chip, CircularProgress, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { SMS_PAGE, smsPageTokens } from '../../theme/smsPageDesign';

export type ScenarioPreviewCardProps = {
  /** Метка в стиле «/start» — короткий идентификатор цепочки */
  badge: string;
  title: string;
  description: string;
  onOpenDetails: () => void;
  onUse: () => void;
  useLabel?: string;
  useLoading?: boolean;
  useDisabled?: boolean;
  isSmsLight: boolean;
  /** Удаление (визуальные цепочки на сервере) */
  onRequestDelete?: () => void;
  deleteDisabled?: boolean;
  /** Подсказка на кнопке удаления (например если удаление заблокировано). */
  deleteTooltip?: string;
  /** Дублирование визуальной цепочки. */
  onRequestDuplicate?: () => void;
  duplicateDisabled?: boolean;
  /** К какой кампании привязана цепочка (пустая строка — не показывать блок). */
  linkedCampaignLine?: string;
  /** Статус карточки, например «В разработке». */
  statusLabel?: string;
  /** Подсказка для недоступной кнопки использования. */
  disabledTooltip?: string;
};

/**
 * Карточка предпросмотра цепочки (как в конструкторе Telegram-ботов):
 * чип, заголовок, описание, ссылка «подробнее», кнопка «Редактирование».
 */
const ScenarioPreviewCard: React.FC<ScenarioPreviewCardProps> = ({
  badge,
  title,
  description,
  onOpenDetails,
  onUse,
  useLabel,
  useLoading = false,
  useDisabled = false,
  isSmsLight,
  onRequestDelete,
  deleteDisabled = false,
  deleteTooltip,
  onRequestDuplicate,
  duplicateDisabled = false,
  linkedCampaignLine,
  statusLabel,
  disabledTooltip,
}) => {
  const { t } = useTranslation();
  const tokens = smsPageTokens(isSmsLight);
  const resolvedUseLabel = useLabel ?? t('scenarioPreview.use');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const hasActions = Boolean(onRequestDelete || onRequestDuplicate);
  const closeMenu = () => setMenuAnchor(null);
  return (
    <Box
      sx={{
        p: { xs: 1.6, sm: 1.75 },
        borderRadius: '12px',
        textAlign: 'left',
        position: 'relative',
        bgcolor: isSmsLight ? '#f2f2f2' : tokens.card,
        border: `1px solid ${tokens.outlineBorder}`,
        boxShadow: tokens.shadow,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
        <Chip
          label={badge}
          size="small"
          sx={{
            height: 28,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: '8px',
            bgcolor: tokens.triggerChipBg,
            color: tokens.triggerChipText,
            maxWidth: 'calc(100% - 44px)',
          }}
        />
        {statusLabel ? (
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              height: 26,
              fontWeight: 700,
              fontSize: 12,
              borderRadius: '999px',
              bgcolor: 'rgba(255, 179, 0, 0.18)',
              color: 'text.primary',
              border: `1px solid ${SMS_PAGE.accent}`,
              ml: 'auto',
            }}
          />
        ) : hasActions ? (
          <>
            <IconButton
              size="small"
              aria-label={t('scenarioPreview.actionsAria')}
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
              }}
              sx={{ mt: -0.5, mr: -0.5 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={menuOpen}
              onClose={closeMenu}
              onClick={(e) => e.stopPropagation()}
              MenuListProps={{ dense: true }}
            >
              {onRequestDuplicate ? (
                <MenuItem
                  disabled={duplicateDisabled}
                  onClick={() => {
                    closeMenu();
                    onRequestDuplicate();
                  }}
                >
                  <ListItemIcon>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('scenarioPreview.duplicateChain')}</ListItemText>
                </MenuItem>
              ) : null}
              {onRequestDelete ? (
                <Tooltip title={deleteDisabled && deleteTooltip ? deleteTooltip : ''} disableHoverListener={!deleteDisabled || !deleteTooltip}>
                  <span>
                    <MenuItem
                      disabled={deleteDisabled}
                      onClick={() => {
                        closeMenu();
                        onRequestDelete();
                      }}
                      sx={{ color: 'error.main' }}
                    >
                      <ListItemIcon sx={{ color: 'error.main' }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('scenarioPreview.deleteChain')}</ListItemText>
                    </MenuItem>
                  </span>
                </Tooltip>
              ) : null}
            </Menu>
          </>
        ) : null}
      </Box>
      <Typography
        variant="h6"
        component="h3"
        sx={{
          fontWeight: 700,
          fontSize: '1.02rem',
          lineHeight: 1.35,
          color: 'text.primary',
          mb: linkedCampaignLine?.trim() ? 0.65 : description.trim() ? 1 : 0.45,
        }}
      >
        {title}
      </Typography>
      {linkedCampaignLine?.trim() ? (
        <Typography
          variant="body2"
          component="p"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.45,
            fontWeight: 500,
            mb: description.trim() ? 0.75 : 1,
            wordBreak: 'break-word',
          }}
        >
          {linkedCampaignLine.trim()}
        </Typography>
      ) : null}
      {description.trim() ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.5,
            mb: 1.25,
            minHeight: 44,
          }}
        >
          {description}
        </Typography>
      ) : null}
      <Button
        variant="text"
        size="small"
        onClick={onOpenDetails}
        sx={{
          p: 0,
          mb: 1.35,
          minWidth: 0,
          textTransform: 'none',
          fontWeight: 500,
          justifyContent: 'flex-start',
          color: 'primary.main',
          '&:hover': { textDecoration: 'underline', bgcolor: 'transparent', color: isSmsLight ? SMS_PAGE.accentHover : undefined },
        }}
      >
        {t('scenarioPreview.viewDetails')}
      </Button>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Tooltip title={useDisabled && disabledTooltip ? disabledTooltip : ''} disableHoverListener={!useDisabled || !disabledTooltip}>
          <span>
            <Button
              variant="outlined"
              size="medium"
              disabled={useDisabled || useLoading}
              onClick={onUse}
              startIcon={useLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
              sx={{
                borderRadius: '100px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                bgcolor: tokens.white,
                borderColor: 'primary.main',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: isSmsLight ? SMS_PAGE.white : tokens.row,
                  borderColor: SMS_PAGE.accentHover,
                },
              }}
            >
              {resolvedUseLabel}
            </Button>
          </span>
        </Tooltip>
        {onRequestDuplicate ? (
          <Button
            variant="text"
            size="medium"
            disabled={duplicateDisabled}
            onClick={onRequestDuplicate}
            startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
            sx={{
              borderRadius: '100px',
              textTransform: 'none',
              fontWeight: 600,
              color: 'primary.main',
              px: 1.5,
            }}
          >
            {t('scenarioPreview.duplicateChain')}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default ScenarioPreviewCard;
